import axios from "axios";
import { marketingHeaders } from "./marketingPrivacy";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API, withCredentials: true });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("vb_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  const lang = (localStorage.getItem("vb_lang") || "es").slice(0, 2);
  config.headers["X-Lang"] = lang === "en" ? "en" : "es";
  if (/^\/(public\/marketing\/meta-|auth\/google|payments\/checkout)/.test(config.url || "")) {
    Object.assign(config.headers, marketingHeaders());
  }
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (e) => {
    const d = e?.response?.data?.detail;
    if (e?.response?.status === 402 && d && (d.error === "quota_exceeded" || d.error === "plan_required")) {
      window.dispatchEvent(new CustomEvent("quota-exceeded", { detail: d }));
    }
    return Promise.reject(e);
  }
);

// Bandera del idioma/país de una voz de narración — como imagen real (flagcdn), no emoji: los
// emoji de bandera no se renderizan de forma fiable en Windows/Chrome sin la fuente Noto Color
// Emoji instalada (se vio en producción: la bandera simplemente no aparecía). Las voces Edge TTS
// traen el locale en el propio id ("es-ES-AlvaroNeural", "en-GB-RyanNeural"...). Las de
// ElevenLabs SÍ traen idioma real (voiceLang, ver GET /voices → labels.language /
// verified_languages en el backend) cuando ElevenLabs lo reporta; solo si ni el id ni voiceLang
// dan una pista usamos el idioma del canal/proyecto como último respaldo.
const LOCALE_COUNTRY = {
  "es-ES": "es", "es-MX": "mx", "es-AR": "ar", "es-CO": "co",
  "en-US": "us", "en-GB": "gb",
};
// Idioma corto de ElevenLabs → país "representativo" para la bandera (un idioma no tiene un
// único país dueño — el español se habla en 20+ países — se elige el más reconocible a simple
// vista). No cubre los ~29 idiomas que soporta ElevenLabs, solo los más probables en la
// biblioteca de un usuario hispanohablante/anglo.
const LANGUAGE_COUNTRY = {
  es: "es", en: "gb", fr: "fr", de: "de", it: "it", pt: "pt",
  pl: "pl", nl: "nl", sv: "se", tr: "tr", ru: "ru", uk: "ua",
  ro: "ro", el: "gr", fi: "fi", da: "dk", cs: "cz", hi: "in",
  ar: "sa", zh: "cn", ja: "jp", ko: "kr",
};
export const voiceFlagUrl = (voiceId, fallbackLang, voiceLang) => {
  const m = /^([a-z]{2}-[A-Z]{2})-/.exec(voiceId || "");
  const locale = m && m[1];
  const cc = (locale && LOCALE_COUNTRY[locale])
    || LANGUAGE_COUNTRY[(voiceLang || "").slice(0, 2).toLowerCase()]
    || (fallbackLang === "en" ? "gb" : "es");
  return `https://flagcdn.com/${cc}.svg`;
};

// Busca una voz concreta del catálogo de ai33 por su id (voice_id de ElevenLabs). Usado para
// mostrar el nombre de una voz ya guardada (default_voice de un canal, narration.voice de un
// proyecto) cuando el llamador no tiene ya cargada la lista completa que la contiene — evita
// duplicar esta búsqueda en VoiceSelect y en OneClickModal (la voz por defecto del canal).
// Devuelve null si no hay id o si la voz no existe en ai33 (no lanza: el llamador decide qué
// mostrar en ese caso, normalmente un aviso).
export const resolveVoiceById = async (id, language = "es") => {
  if (!id) return null;
  try {
    const { data } = await api.get("/voices/catalog", { params: { search: id, language, page_size: 1 } });
    return data?.voices?.[0] || null;
  } catch {
    return null;
  }
};

export const apiErr = (e, fallback = "Ocurrió un error. Intenta de nuevo.") => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  return d?.msg || d?.message || e?.message || fallback;
};

export const fmtTime = (sec) => {
  sec = Math.round(sec || 0);
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

export const fmtTimeLong = (sec) => {
  sec = Math.round(sec || 0);
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  if (m === 0) return `${s} segundos`;
  return `${m} min ${s} s`;
};

// Formato de reloj mm:ss (p.ej. 13:59, 09:30, 240:00) para mostrar el saldo/coste.
export const fmtClock = (sec) => {
  sec = Math.max(0, Math.round(sec || 0));
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

// Formato unificado del saldo en minutos (p.ej. "84 min") — una sola unidad en toda la UI,
// en vez de mezclar segundos/mm:ss/tiempo largo según la pantalla.
export const fmtMinutes = (sec) => `${Math.round(Math.max(0, sec || 0) / 60)} min`;

// Saldo/coste en créditos (ago-2026): 1 crédito = 1 segundo guardado, SIN reescalar — a
// diferencia de fmtClock/fmtMinutes (que formatean como tiempo), esto es solo el número, con
// separador de miles. La palabra "créditos"/"credits" la pone la traducción (t("common.credits",
// {count})), no esta función, para que cambie con el idioma — mismo motivo por el que no se
// reescala a minutos: una miniatura o una verificación de B-roll cuestan del orden de segundos,
// y redondear a minutos las mostraría como "0 créditos" pese a tener un coste real.
export const fmtCredits = (sec) => Math.round(Math.max(0, sec || 0)).toLocaleString("es-ES");

export const fmtCreditBalance = (credits) => new Intl.NumberFormat("es-ES", { maximumFractionDigits: 0, useGrouping: "always" }).format(Math.floor(Math.max(0, Number(credits) || 0)));

// Formato legible de bytes (p.ej. 512 MB, 2.3 GB) para mostrar cuotas de almacenamiento.
export const fmtBytes = (bytes) => {
  bytes = Math.max(0, bytes || 0);
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${Math.round(bytes / (1024 * 1024))} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

// Coste de acciones de IA en segundos: coste real estimado (USD) × markup → segundos.
// Espejo exacto de la lógica del backend (script_cost / analyze_cost).
const AI_PRICE = { sonnet: { in: 3.0, out: 15.0 }, haiku: { in: 0.80, out: 4.0 } };

// Ancla de precio: la MANDA el backend en /quota (`pricing`), no se calcula aquí. Estos dos
// valores son solo el arranque, para el primer pintado antes de que /quota responda; en cuanto
// llega, setPricingFromQuota los sustituye por los que de verdad se usan al cobrar.
// Por qué: antes estaban copiados a mano y se desfasaron —el plan Starter pasó de 9€/1h a
// 19€/2h y esta copia se quedó atrás, así que TODOS los badges de coste de la app enseñaban un
// ~5% más de segundos de los que se descontaban—. Y el markup es editable desde Admin en
// caliente, o sea que copiarlo estaba condenado a desfasarse otra vez.
let AI_MARKUP = 3;
let USD_PER_SECOND = (20 / 3600) / 0.92;

export const setPricingFromQuota = (quota) => {
  const p = quota?.pricing;
  if (!p) return;
  if (p.usd_per_second > 0) USD_PER_SECOND = p.usd_per_second;
  if (p.ai_markup > 0) AI_MARKUP = p.ai_markup;
};

const _llmUsd = (tier, inT, outT) => (inT * AI_PRICE[tier].in + outT * AI_PRICE[tier].out) / 1_000_000;
const _usdToSeconds = (usd) => Math.round((usd * AI_MARKUP) / USD_PER_SECOND);
const _tokFromChars = (c) => Math.ceil((c || 0) / 3.5);

// One temporal contact-sheet assessment; backend youtube_interval_review_cost.
export const youtubeIntervalReviewCostSeconds = () =>
  Math.max(1, _usdToSeconds(_llmUsd("haiku", 4500, 450)));

// Estimate by requested coverage, never cap the number of source videos.
export const youtubeFootageCostSeconds = (seconds) => {
  const cuts = Math.ceil(Math.max(0, Number(seconds) || 0) / 6);
  return youtubeIntervalReviewCostSeconds() * (cuts + Math.ceil(cuts / 3));
};

export const scriptCostSeconds = (minutes) => {
  const m = Math.max(1, Math.min(120, parseInt(minutes, 10) || 20));
  const words = m * 145;
  let usd = _llmUsd("sonnet", 6000, Math.round(words * 5));
  usd += _llmUsd("sonnet", _tokFromChars(words * 6), 1800);
  return _usdToSeconds(usd);
};

export const analyzeCostSeconds = (chars) => {
  const n = parseInt(chars, 10) || 0;
  if (n <= 0) return 0;
  const usd = _llmUsd("sonnet", _tokFromChars(n), Math.min(4000, Math.max(400, Math.round(n / 6))));
  return Math.max(1, _usdToSeconds(usd));
};

// Evaluación de calidad del guion (Sonnet: nota + evidencias + técnicas de retención).
// Espejo de quality_eval_cost del backend.
export const qualityEvalCostSeconds = (chars) => {
  const n = parseInt(chars, 10) || 0;
  if (n <= 0) return 0;
  const usd = _llmUsd("sonnet", _tokFromChars(n), 1800);
  return Math.max(1, _usdToSeconds(usd));
};

export const recommendationsCostSeconds = () => Math.max(1, _usdToSeconds(_llmUsd("sonnet", 6000, 1500)));

// Coste TOTAL del pipeline "Crear vídeo en 1 clic": guion (Sonnet) + render (n·60 segundos
// equivalen a la duración estimada del vídeo final; el backend cobra dentro de run_render
// esos mismos segundos vía deduct_seconds). La narración TTS se cobra dentro de run_narration
// por caracteres reales del guion — para el badge lo aproximamos incluido en el render (la
// diferencia es pequeña frente al render y evita mostrar dos números confusos). Este número
// coincide con el pre-chequeo `require_seconds(uid, duracion_min*60, "one_click_precheck")`
// más el `script_cost` que se descuenta al empezar el guion.
// Plan de montaje con IA (transiciones, movimiento de cámara y rótulos — ver plan_montaje en el
// backend). Espejo de montaje_cost_minutes: el brief que ve el montador son ~550 caracteres por
// minuto de vídeo (~700 de texto narrado por escena más el inventario de planos y rótulos, a ~2
// escenas por minuto). Es una estimación: el cobro real usa el brief con el guion ya escrito.
const MONTAJE_CHARS_PER_MIN = 550;
export const montajeCostSeconds = (minutes) => {
  const m = Math.max(1, parseInt(minutes, 10) || 20);
  const n = m * MONTAJE_CHARS_PER_MIN;
  const usd = _llmUsd("sonnet", _tokFromChars(n), Math.min(4000, Math.max(600, Math.round(n / 8))));
  return Math.max(1, _usdToSeconds(usd));
};

export const oneClickCostSeconds = (minutes) => {
  const m = Math.max(5, Math.min(60, parseInt(minutes, 10) || 20));
  return scriptCostSeconds(m) + m * 60 + montajeCostSeconds(m);
};

export const narrationPremiumCostSeconds = (chars) => {
  const usd = (Math.max(0, parseInt(chars, 10) || 0) / 1000) * 0.03;
  return Math.max(1, _usdToSeconds(usd));
};

export const calendarCostSeconds = (numTitles) => {
  const n = Math.max(1, parseInt(numTitles, 10) || 1);
  return Math.max(1, Math.round((recommendationsCostSeconds() * n) / 10));
};

const THUMB_IMAGE_USD = 0.04; // coste estimado por imagen Nano Banana
export const thumbnailCostSeconds = (count = 1) => {
  const n = Math.max(1, Math.min(3, parseInt(count, 10) || 1));
  return Math.max(1, _usdToSeconds(THUMB_IMAGE_USD)) * n;
};

// Modo de pruebas de Stripe forzado desde Admin (ver StripePricesTab). El backend vuelve a
// comprobar que quien lo pide es admin (server.py::_resolve_live_mode) -- este flag en
// localStorage solo evita mandar force_test:true en cada llamada de pago, no es la barrera de
// seguridad real.
const TEST_MODE_KEY = "vb_admin_force_test_stripe";
export const isAdminTestModeForced = () => localStorage.getItem(TEST_MODE_KEY) === "1";
export const setAdminTestModeForced = (on) => {
  if (on) localStorage.setItem(TEST_MODE_KEY, "1");
  else localStorage.removeItem(TEST_MODE_KEY);
};

export default api;
